import { Alert, Box, FormControl, FormHelperText, InputLabel } from '@chronosphereio/chrono-ui';
import { PluginKindSelect } from '../../PluginKindSelect';
import { PluginSpecEditor } from '../PluginSpecEditor';
import { PluginEditorProps, usePluginEditor } from './plugin-editor-api';

/**
 * A combination `PluginKindSelect` and `PluginSpecEditor` component. This is meant for editing the `plugin` property
 * that's common in our JSON specs where a user selects a plugin `kind` and then edits the `spec` via that plugin's
 * editor component. It takes care of transitioning from one plugin kind to another "all at once" so that when the
 * plugin's kind changes, the spec is also changed at the same time so those options editor components don't see a
 * previous plugin's spec state. If you just want this behavior, but in a different UI layout from this, try the
 * `usePluginEditor` hook that powers this component.
 */
export function PluginEditor(props: PluginEditorProps) {
  const {
    value,
    pluginType,
    pluginKindLabel,
    onChange: _,
    isReadonly,
    dataType,
    hidePluginKindSelector,
    variableOpportunities,
    ...others
  } = props;
  const { pendingKind, isLoading, error, onKindChange, onSpecChange } = usePluginEditor(props);
  return (
    <Box {...others}>
      {value.kind === '' && <Alert severity="info">Select a {pluginKindLabel}</Alert>}

      {hidePluginKindSelector !== true && (
        <FormControl margin="dense" fullWidth={false} disabled={isLoading} error={error !== null} sx={{ mb: 1 }}>
          {/* TODO: How to ensure ids are unique? */}
          <InputLabel id="plugin-kind-label">{pluginKindLabel}</InputLabel>
          <PluginKindSelect
            labelId="plugin-kind-label"
            label={pluginKindLabel}
            pluginType={pluginType}
            value={pendingKind !== '' ? pendingKind : value.kind}
            readOnly={isReadonly}
            onChange={onKindChange}
          />
          <FormHelperText>{error?.message ?? ''}</FormHelperText>
        </FormControl>
      )}
      <div>
        <PluginSpecEditor
          pluginType={pluginType}
          pluginKind={value.kind}
          value={value.spec}
          onChange={onSpecChange}
          isReadonly={isReadonly}
          dataType={dataType}
          variableOpportunities={variableOpportunities}
        />
      </div>
    </Box>
  );
}
